import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import { formatDuration } from '../lib/examStats';
import SEO from '../components/SEO';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../firebase';

/* ── Helpers ── */
function computeXP(totalCorrect: number, totalSessions: number, streak: number) {
  return totalCorrect * 10 + totalSessions * 50 + streak * 25;
}
function computeLevel(xp: number) {
  const level = Math.floor(xp / 500) + 1;
  const xpIntoLevel = xp % 500;
  const xpForNext = 500;
  return { level, xpIntoLevel, xpForNext, progress: (xpIntoLevel / xpForNext) * 100 };
}
function computeStreak(sessions: any[]): number {
  if (!sessions.length) return 0;
  const days = new Set(sessions.map(s => new Date(s.created_at).toISOString().slice(0, 10)));
  let streak = 0;
  const today = new Date();
  for (let i = 0; i < 365; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const ds = d.toISOString().slice(0, 10);
    if (days.has(ds)) streak++;
    else if (i > 0) break;
  }
  return streak;
}
function computeReadiness(accuracy: number, totalQuestions: number, bestScore: number) {
  const accWeight = Math.min(accuracy / 80, 1) * 40;
  const volWeight = Math.min(totalQuestions / 180, 1) * 30;
  const scoreWeight = Math.min(bestScore / 70, 1) * 30;
  return Math.round(accWeight + volWeight + scoreWeight);
}
function computePassProb(readiness: number) {
  if (readiness >= 85) return 'High';
  if (readiness >= 60) return 'Moderate';
  if (readiness >= 30) return 'Developing';
  return 'Early Stage';
}
function getBadge(xp: number, streak: number, sessions: number, accuracy: number) {
  const badges = [];
  if (sessions >= 1) badges.push({ name: 'First Steps', icon: '🎯', color: 'bg-blue-500' });
  if (sessions >= 10) badges.push({ name: 'Dedicated', icon: '🔥', color: 'bg-orange-500' });
  if (accuracy >= 70) badges.push({ name: 'Sharp Shooter', icon: '⚡', color: 'bg-yellow-500' });
  if (streak >= 3) badges.push({ name: 'On Fire', icon: '🌟', color: 'bg-red-500' });
  if (streak >= 7) badges.push({ name: 'Week Warrior', icon: '🏆', color: 'bg-purple-500' });
  if (xp >= 1000) badges.push({ name: 'Scholar', icon: '🎓', color: 'bg-indigo-500' });
  if (xp >= 3000) badges.push({ name: 'PMP Master', icon: '👑', color: 'bg-amber-500' });
  return badges;
}

/* ── SVG Charts ── */
function MiniSparkline({ data, color }: { data: number[]; color: string }) {
  if (data.length < 2) data = [0, ...data];
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const w = 100, h = 32;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * h}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-8" preserveAspectRatio="none">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BarChartMini({ data, color }: { data: number[]; color: string }) {
  const max = Math.max(...data, 1);
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((v, i) => (
        <div key={i} className="flex-1 rounded-t transition-all duration-500" style={{ height: `${(v / max) * 100}%`, backgroundColor: color, opacity: 0.3 + (i / data.length) * 0.7 }} />
      ))}
    </div>
  );
}

function RadialProgress({ value, size = 120, label, sublabel }: { value: number; size?: number; label: string; sublabel?: string }) {
  const r = (size - 16) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth="8" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="url(#grad-readiness)" strokeWidth="8" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset} className="transition-all duration-1000 ease-out" />
        <defs>
          <linearGradient id="grad-readiness" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="50%" stopColor="#8b5cf6" />
            <stop offset="100%" stopColor="#ec4899" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold">{value}%</span>
        <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</span>
        {sublabel && <span className="text-[10px] text-muted-foreground mt-0.5">{sublabel}</span>}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const location = useLocation();
  const lastRefreshRef = useRef<number>(0);
  const [analytics, setAnalytics] = useState({
    totalSessions: 0, totalQuestions: 0, totalCorrect: 0, totalIncorrect: 0,
    totalUnanswered: 0, accuracy: 0, averageSecondsPerQuestion: 0,
    bestScore: 0, latestScore: 0, examSessions: 0, trainingSessions: 0,
    trend: 'new' as 'up' | 'down' | 'stable' | 'new', sessions: [] as any[],
  });
  const [failedQuestions, setFailedQuestions] = useState<any[]>([]);
  const [notAnsweredQuestions, setNotAnsweredQuestions] = useState<any[]>([]);
  const [domainStats, setDomainStats] = useState<Record<string, { correct: number; total: number }>>({});
  const [dataLoading, setDataLoading] = useState(false);
  const [reviewFilter, setReviewFilter] = useState<'failed' | 'notAnswered'>('failed');
  const [scopeFilter, setScopeFilter] = useState<'overall' | 'session'>('overall');
  const [selectedSessionId, setSelectedSessionId] = useState<string>('all');
  const [dashboardReference, setDashboardReference] = useState<'PMBOK 07' | 'PMBOK 08'>('PMBOK 08');
    const [totalPmbok07Questions, setTotalPmbok07Questions] = useState(0);
  const [totalPmbok08Questions, setTotalPmbok08Questions] = useState(0);

  // Refresh data when navigating back to dashboard (e.g., after retrying failed questions)
  useEffect(() => {
    if (!user?.firebase_uid) return;
    const now = Date.now();
    // Only refresh if more than 2 seconds since last refresh (prevents duplicate calls)
    if (now - lastRefreshRef.current < 2000) return;
    lastRefreshRef.current = now;
    const loadData = async () => {
      setDataLoading(true);
      try {
        // Fetch questions, attempts, sessions in parallel — no aggregation queries (avoid 429)
        const allQuestionsPromise = getDocs(query(collection(db, 'questions'), limit(2000)));
        const attemptsPromise = getDocs(query(collection(db, 'attempts'), where('userId', '==', user.firebase_uid)));
        const sessionsPromise = getDocs(query(collection(db, 'exam_sessions'), where('userId', '==', user.firebase_uid)));

        const [allQuestionsSnap, attemptsSnap, sessionsSnap] = await Promise.all([
          allQuestionsPromise, attemptsPromise, sessionsPromise,
        ]);

        let pmbok07Count = 0;
        let pmbok08Count = 0;
        const refQuestionIds = new Set<string>();
        const domainMap = new Map<string, string>();
        const allQs: any[] = [];
        allQuestionsSnap.docs.forEach((doc) => {
          const q = doc.data();
          const qId = q.question_id || String(q.id);
          // Treat missing/null/undefined reference as PMBOK 08 (legacy docs)
          const isPmbok07 = q.reference === 'PMBOK 07';
          const isPmbok08 = q.reference === 'PMBOK 08' || !q.reference || q.reference === null || q.reference === undefined;
          if (isPmbok07) pmbok07Count++;
          if (isPmbok08) pmbok08Count++;
          // Client-side filter for the selected reference
          const matchesRef = dashboardReference === 'PMBOK 07' ? isPmbok07 : isPmbok08;
                    if (!matchesRef) return;
          if (q.domain) domainMap.set(qId, q.domain);
          refQuestionIds.add(qId);
          allQs.push({ ...q, id: doc.id });
        });
        setTotalPmbok07Questions(pmbok07Count);
        setTotalPmbok08Questions(pmbok08Count);
        console.log(`Total PMBOK 07 questions found: ${pmbok07Count}`);
        console.log(`Total PMBOK 08 questions found: ${pmbok08Count}`);
        console.log('Dashboard counts — PMBOK 07:', pmbok07Count, 'PMBOK 08:', pmbok08Count, 'selected:', dashboardReference);

        // Filter attempts by reference: use reference field if present, otherwise check questionId against refQuestionIds
        const attempts = attemptsSnap.docs.map(d => d.data()).filter((a: any) => {
          if (a.reference) return a.reference === dashboardReference;
          return refQuestionIds.has(a.questionId);
        }) as any[];
        // Filter sessions by reference: use reference field if present, otherwise check via attempts' sessionIds
        const validSessionIds = new Set(attempts.map(a => a.sessionId));
        const sessions = sessionsSnap.docs.map(d => d.data()).filter((s: any) => {
          if (s.reference) return s.reference === dashboardReference;
          return validSessionIds.has(s.session_id);
        }) as any[];

        const attemptsByQuestion = new Map<string, any[]>();
        attempts.forEach((a: any) => {
          const qId = a.questionId;
          if (!attemptsByQuestion.has(qId)) attemptsByQuestion.set(qId, []);
          attemptsByQuestion.get(qId)!.push(a);
        });
        let totalCorrect = 0, totalIncorrect = 0, totalDuration = 0, totalAnswered = 0;
        const domains: Record<string, { correct: number; total: number }> = {};
        attemptsByQuestion.forEach((qAttempts) => {
          qAttempts.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
          const latest = qAttempts[0];
          totalDuration += latest.time_spent_seconds || latest.timeSpent || 0;
          totalAnswered++;
          const isCorrect = latest.is_correct === 1 || latest.is_correct === true || latest.isCorrect === true;
          if (isCorrect) totalCorrect++; else totalIncorrect++;
          const qId = latest.questionId || latest.question_id;
          const dom = latest.domain || domainMap.get(qId) || 'Unknown';
          if (!domains[dom]) domains[dom] = { correct: 0, total: 0 };
          domains[dom].total++;
          if (isCorrect) domains[dom].correct++;
        });
        const totalSessions = sessions.length;
        const accuracy = totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0;
        const averageSecondsPerQuestion = totalAnswered > 0 ? Math.round(totalDuration / totalAnswered) : 0;
        const bestScore = sessions.reduce((best: number, s: any) => Math.max(best, s.score), 0);
        const latestScore = sessions[0]?.score || 0;
        const previousScore = sessions[1]?.score;
        const examSessions = sessions.filter((s: any) => s.mode === 'exam').length;
        const trainingSessions = sessions.filter((s: any) => s.mode === 'training').length;
        const trend = previousScore === undefined ? (totalSessions > 0 ? 'stable' : 'new') : latestScore > previousScore ? 'up' : latestScore < previousScore ? 'down' : 'stable';
        setAnalytics({ totalSessions, totalQuestions: totalAnswered, totalCorrect, totalIncorrect, totalUnanswered: 0, accuracy, averageSecondsPerQuestion, bestScore, latestScore, examSessions, trainingSessions, trend, sessions });
        const failed: any[] = [];
        attemptsByQuestion.forEach((qAttempts) => {
          qAttempts.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
          const latest = qAttempts[0];
          // Only count as failed if is_correct is explicitly false (0 or false)
          // Undefined/null values should not be counted as failed
          const isCorrect = latest.is_correct === 1 || latest.is_correct === true || latest.isCorrect === true;
          const isIncorrect = latest.is_correct === 0 || latest.is_correct === false || latest.isCorrect === false;
          console.log('Dashboard - Question ID:', latest.questionId, 'isCorrect:', isCorrect, 'isIncorrect:', isIncorrect, 'is_correct value:', latest.is_correct, 'isCorrect value:', latest.isCorrect);
          // Also verify the question exists in the filtered question set for the selected reference
          const qId = latest.questionId || latest.question_id;
          const questionExistsInRef = refQuestionIds.has(qId);
          if (isIncorrect && questionExistsInRef) {
            failed.push(latest);
          }
        });
        console.log('Dashboard - Total failed questions:', failed.length);
        setFailedQuestions(failed);

        // Compute not-answered questions (questions in DB with no attempts by this user)
        // Only include questions that are in the filtered reference set
        const attemptedQIds = new Set(attempts.map(a => a.questionId));
        const notAnswered = allQs.filter(q => {
          const qId = q.question_id || String(q.id);
          return !attemptedQIds.has(qId) && refQuestionIds.has(qId);
        });
        setNotAnsweredQuestions(notAnswered);

        setDomainStats(domains);
      } catch (error) { console.error('Failed to load dashboard data:', error); }
      finally { setDataLoading(false); }
    };
    loadData();
  }, [user?.firebase_uid, location.pathname, location.key, dashboardReference]);

  /* ── Derived metrics ── */
  const streak = useMemo(() => computeStreak(analytics.sessions), [analytics.sessions]);
  const xp = useMemo(() => computeXP(analytics.totalCorrect, analytics.totalSessions, streak), [analytics.totalCorrect, analytics.totalSessions, streak]);
  const levelInfo = useMemo(() => computeLevel(xp), [xp]);
  const readiness = useMemo(() => computeReadiness(analytics.accuracy, analytics.totalQuestions, analytics.bestScore), [analytics.accuracy, analytics.totalQuestions, analytics.bestScore]);
  const passProb = useMemo(() => computePassProb(readiness), [readiness]);
  const badges = useMemo(() => getBadge(xp, streak, analytics.totalSessions, analytics.accuracy), [xp, streak, analytics.totalSessions, analytics.accuracy]);
  const accuracyTrend = useMemo(() => analytics.sessions.slice(0, 10).reverse().map(s => s.score), [analytics.sessions]);
  const questionsPerSession = useMemo(() => analytics.sessions.slice(0, 10).reverse().map(s => s.correct + s.incorrect), [analytics.sessions]);

  // Compute filtered review questions based on filter selections
  const filteredReviewQuestions = useMemo(() => {
    if (reviewFilter === 'failed') {
      if (scopeFilter === 'overall' || selectedSessionId === 'all') {
        return failedQuestions;
      }
      return failedQuestions.filter(q => q.sessionId === selectedSessionId);
    } else {
      // Not answered questions — overall shows all, per-session also shows all
      // (since not-answered means no attempt exists in any session)
      return notAnsweredQuestions;
    }
  }, [reviewFilter, scopeFilter, selectedSessionId, failedQuestions, notAnsweredQuestions]);

  const ecoDomains = [
    { key: 'People', label: t('dashboard.people'), pct: 33, color: 'from-blue-500 to-cyan-500', icon: '👥' },
    { key: 'Process', label: t('dashboard.process'), pct: 41, color: 'from-violet-500 to-purple-500', icon: '⚙️' },
    { key: 'Business Environment', label: t('dashboard.businessEnvironment'), pct: 26, color: 'from-emerald-500 to-teal-500', icon: '📊' },
  ];

  // Time assessment: Ideal is 80 seconds per question (240 min / 180 questions)
  const idealTimePerQuestion = 80;
  const timeRatio = analytics.averageSecondsPerQuestion > 0 ? analytics.averageSecondsPerQuestion / idealTimePerQuestion : 0;
  let timeAssessment = 'On Track';
  let timeColor = 'text-emerald-600 dark:text-emerald-400';
  let timeBg = 'from-emerald-500/10 to-emerald-500/5';
  if (timeRatio > 1.5) {
    timeAssessment = 'Too Slow';
    timeColor = 'text-red-600 dark:text-red-400';
    timeBg = 'from-red-500/10 to-red-500/5';
  } else if (timeRatio > 1.2) {
    timeAssessment = 'Slow';
    timeColor = 'text-amber-600 dark:text-amber-400';
    timeBg = 'from-amber-500/10 to-amber-500/5';
  } else if (timeRatio < 0.6) {
    timeAssessment = 'Too Fast';
    timeColor = 'text-blue-600 dark:text-blue-400';
    timeBg = 'from-blue-500/10 to-blue-500/5';
  }

  const kpiCards = [
    { label: t('dashboard.questionsSolved'), value: analytics.totalQuestions, icon: '📝', bg: 'from-blue-500/10 to-blue-500/5', text: 'text-blue-600 dark:text-blue-400', spark: accuracyTrend, sparkColor: '#3b82f6' },
    { label: t('dashboard.accuracy'), value: `${analytics.accuracy}%`, icon: '🎯', bg: 'from-emerald-500/10 to-emerald-500/5', text: 'text-emerald-600 dark:text-emerald-400', spark: accuracyTrend, sparkColor: '#10b981' },
    { label: t('dashboard.bestScore'), value: `${analytics.bestScore}%`, icon: '🏆', bg: 'from-amber-500/10 to-amber-500/5', text: 'text-amber-600 dark:text-amber-400', spark: accuracyTrend, sparkColor: '#f59e0b' },
    { label: t('dashboard.studyStreak'), value: `${streak} ${streak === 1 ? t('dashboard.day') : t('dashboard.days')}`, icon: '🔥', bg: 'from-orange-500/10 to-orange-500/5', text: 'text-orange-600 dark:text-orange-400', spark: questionsPerSession, sparkColor: '#f97316' },
  ];

  const passProbKey = passProb === 'High' ? 'high' : passProb === 'Moderate' ? 'moderate' : passProb === 'Developing' ? 'developing' : 'earlyStage';
  const passProbColor = passProb === 'High' ? 'text-emerald-500' : passProb === 'Moderate' ? 'text-blue-500' : passProb === 'Developing' ? 'text-amber-500' : 'text-muted-foreground';
  const passProbBg = passProb === 'High' ? 'from-emerald-500/20 to-emerald-500/5' : passProb === 'Moderate' ? 'from-blue-500/20 to-blue-500/5' : passProb === 'Developing' ? 'from-amber-500/20 to-amber-500/5' : 'from-muted/20 to-muted/5';

  const weakestDomain = Object.entries(domainStats).sort((a, b) => a[1].correct / a[1].total - b[1].correct / b[1].total)[0]?.[0] || t('dashboard.process');
  const coachAdvice = analytics.totalSessions === 0
    ? t('dashboard.coachWelcome')
    : analytics.accuracy >= 80
    ? t('dashboard.coachExcellent', { accuracy: analytics.accuracy, prob: t(`dashboard.${passProbKey}`) })
    : analytics.accuracy >= 60
    ? t('dashboard.coachGood', { accuracy: analytics.accuracy, domain: weakestDomain })
    : analytics.totalQuestions > 0
    ? t('dashboard.coachLow', { accuracy: analytics.accuracy })
    : t('dashboard.coachStart');

  return (
    <>
      <SEO title={t('dashboard.welcome')} description="View your PMP exam practice statistics, track your progress, and review failed questions in your personal dashboard." noindex />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {dataLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">{t('dashboard.loadingDashboard')}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* ═══ Filters ═══ */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
              <div className="inline-flex items-center rounded-2xl border border-border bg-card p-1.5 shadow-sm">
                {(['PMBOK 07', 'PMBOK 08'] as const).map((ref) => (
                  <button
                    key={ref}
                    onClick={() => { setDashboardReference(ref); lastRefreshRef.current = 0; }}
                    className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${
                      dashboardReference === ref
                        ? 'bg-primary text-primary-foreground shadow-md'
                        : 'text-muted-foreground hover:bg-accent/50'
                    }`}
                  >
                    {ref}
                  </button>
                ))}
              </div>
                          </div>

            {/* ═══ Hero Section ═══ */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-hero p-6 lg:p-8 text-white shadow-premium-lg animate-fade-in">
              <div className="absolute inset-0 bg-black/10" />
              <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
              <div className="absolute -left-10 -bottom-10 w-48 h-48 rounded-full bg-white/5 blur-2xl" />
              <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/20 backdrop-blur-sm">{t('dashboard.level')} {levelInfo.level}</span>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/20 backdrop-blur-sm">{xp} {t('dashboard.xp')}</span>
                    {streak > 0 && <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-500/40 backdrop-blur-sm">🔥 {t('dashboard.dayStreak', { count: streak })}</span>}
                  </div>
                  <h1 className="text-2xl lg:text-3xl font-bold mb-1">
                    {t('dashboard.welcome')}, {user?.display_name || user?.email?.split('@')[0] || 'Learner'}
                  </h1>
                  <p className="text-white/80 text-sm lg:text-base mb-4">
                    {analytics.totalSessions === 0 ? t('dashboard.readyToBegin') : t('dashboard.youAnswered', { count: analytics.totalQuestions, accuracy: analytics.accuracy })}
                  </p>
                  {/* XP Bar */}
                  <div className="max-w-xs">
                    <div className="flex items-center justify-between text-xs text-white/70 mb-1">
                      <span>{t('dashboard.level')} {levelInfo.level}</span>
                      <span>{levelInfo.xpIntoLevel} / {levelInfo.xpForNext} {t('dashboard.xp')}</span>
                    </div>
                    <div className="h-2 rounded-full bg-white/20 overflow-hidden">
                      <div className="h-full rounded-full bg-white transition-all duration-1000 ease-out" style={{ width: `${levelInfo.progress}%` }} />
                    </div>
                  </div>
                </div>
                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                  <Link to={`/exam?reference=${dashboardReference}`} className="px-6 py-3 bg-white text-primary rounded-xl font-semibold text-sm hover:bg-white/90 transition-all hover:scale-105 shadow-lg flex items-center justify-center gap-2">
                    <span>▶</span> {t('dashboard.startExam')}
                  </Link>
                  <Link to={`/exam?reference=${dashboardReference}&mode=training`} className="px-6 py-3 bg-white/15 text-white rounded-xl font-semibold text-sm hover:bg-white/25 transition-all backdrop-blur-sm border border-white/20 flex items-center justify-center gap-2">
                    {t('exam.trainingMode')}
                  </Link>
                </div>
              </div>
            </div>

            {/* ═══ KPI Cards ═══ */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {kpiCards.map((kpi, i) => (
                <div key={kpi.label} className={`relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br ${kpi.bg} p-5 shadow-premium hover:shadow-premium-lg transition-all duration-300 hover:-translate-y-0.5 animate-slide-up animate-stagger-${i + 1}`}>
                  <div className="flex items-start justify-between mb-3">
                    <span className="text-2xl">{kpi.icon}</span>
                    <span className={`text-2xl font-bold ${kpi.text}`}>{kpi.value}</span>
                  </div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">{kpi.label}</p>
                  {kpi.spark.length > 0 && <MiniSparkline data={kpi.spark} color={kpi.sparkColor} />}
                </div>
              ))}
              {/* Time Assessment Card */}
              <div className={`relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br ${timeBg} p-5 shadow-premium hover:shadow-premium-lg transition-all duration-300 hover:-translate-y-0.5 animate-slide-up animate-stagger-5`}>
                <div className="flex items-start justify-between mb-3">
                  <span className="text-2xl">⏱</span>
                  <span className={`text-2xl font-bold ${timeColor}`}>{formatDuration(analytics.averageSecondsPerQuestion)}</span>
                </div>
                <p className="text-xs font-medium text-muted-foreground mb-2">{t('dashboard.avgTimePerQuestion')}</p>
                <div className="flex items-center justify-between text-xs">
                  <span className={`font-medium ${timeColor}`}>{timeAssessment}</span>
                  <span className="text-muted-foreground">Ideal: 1m 20s</span>
                </div>
              </div>
              {/* Total PMBOK Questions Card */}
              <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-indigo-500/10 to-indigo-500/5 p-5 shadow-premium hover:shadow-premium-lg transition-all duration-300 hover:-translate-y-0.5 animate-slide-up animate-stagger-6">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-2xl">📚</span>
                  <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                    {dashboardReference === 'PMBOK 07' ? totalPmbok07Questions : totalPmbok08Questions}
                  </span>
                </div>
                <p className="text-xs font-medium text-muted-foreground mb-2">{t('dashboard.totalPmbokQuestions')}</p>
                <div className="flex items-center justify-between text-xs">
                  <span className={`font-medium ${dashboardReference === 'PMBOK 07' ? 'text-indigo-600 dark:text-indigo-400' : 'text-muted-foreground'}`}>PMBOK 07: {totalPmbok07Questions}</span>
                  <span className={`font-medium ${dashboardReference === 'PMBOK 08' ? 'text-indigo-600 dark:text-indigo-400' : 'text-muted-foreground'}`}>PMBOK 08: {totalPmbok08Questions}</span>
                </div>
              </div>
            </div>

            {/* ═══ Readiness + Pass Probability + Gamification ═══ */}
            <div className="grid lg:grid-cols-3 gap-4">
              {/* Readiness Score */}
              <div className="rounded-2xl border border-border bg-card p-6 shadow-premium animate-slide-up animate-stagger-1">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">{t('dashboard.examReadiness')}</h3>
                <div className="flex flex-col items-center">
                  <RadialProgress value={readiness} label={t('dashboard.ready')} sublabel={t(`dashboard.${passProbKey}`)} />
                  <div className="mt-4 w-full space-y-2">
                    <div className="flex justify-between text-xs"><span className="text-muted-foreground">{t('dashboard.accuracyLabel')}</span><span className="font-medium">{analytics.accuracy}%</span></div>
                    <div className="flex justify-between text-xs"><span className="text-muted-foreground">{t('dashboard.volume')}</span><span className="font-medium">{Math.round((analytics.totalQuestions / 180) * 100)}%</span></div>
                    <div className="flex justify-between text-xs"><span className="text-muted-foreground">{t('dashboard.bestScore')}</span><span className="font-medium">{analytics.bestScore}%</span></div>
                  </div>
                </div>
              </div>

              {/* Pass Probability */}
              <div className={`rounded-2xl border border-border bg-gradient-to-br ${passProbBg} p-6 shadow-premium animate-slide-up animate-stagger-2`}>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">{t('dashboard.passProbability')}</h3>
                <div className="flex flex-col items-center justify-center py-4">
                  <span className={`text-4xl font-bold ${passProbColor}`}>{t(`dashboard.${passProbKey}`)}</span>
                  <div className="mt-3 w-full">
                    <div className="h-3 rounded-full bg-muted overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-1000 ${passProb === 'High' ? 'bg-emerald-500' : passProb === 'Moderate' ? 'bg-blue-500' : passProb === 'Developing' ? 'bg-amber-500' : 'bg-muted-foreground'}`} style={{ width: `${Math.max(readiness, 8)}%` }} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                      {readiness >= 85 ? t('dashboard.examReadyMsg') : readiness >= 60 ? t('dashboard.onTrackMsg') : readiness >= 30 ? t('dashboard.buildingFoundation') : t('dashboard.justStarted')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Gamification */}
              <div className="rounded-2xl border border-border bg-card p-6 shadow-premium animate-slide-up animate-stagger-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">{t('dashboard.achievements')}</h3>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    {levelInfo.level}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{t('dashboard.level')} {levelInfo.level}</p>
                    <p className="text-xs text-muted-foreground">{xp} {t('dashboard.totalXP')}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {badges.length > 0 ? badges.map(b => (
                    <div key={b.name} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted/50 text-xs font-medium" title={b.name}>
                      <span className={`w-5 h-5 rounded-full ${b.color} flex items-center justify-center text-[10px]`}>{b.icon}</span>
                      {b.name}
                    </div>
                  )) : <p className="text-xs text-muted-foreground">{t('dashboard.completeSessionBadge')}</p>}
                </div>
              </div>
            </div>

            {/* ═══ Charts Row ═══ */}
            <div className="grid lg:grid-cols-2 gap-4">
              {/* Accuracy Trend */}
              <div className="rounded-2xl border border-border bg-card p-6 shadow-premium animate-slide-up animate-stagger-1">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">{t('dashboard.accuracyTrend')}</h3>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${analytics.trend === 'up' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400' : analytics.trend === 'down' ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400' : 'bg-muted text-muted-foreground'}`}>
                    {analytics.trend === 'up' ? `↗ ${t('dashboard.improving')}` : analytics.trend === 'down' ? `↘ ${t('dashboard.needsFocus')}` : analytics.trend === 'stable' ? `→ ${t('dashboard.stable')}` : `★ ${t('dashboard.new_')}`}
                  </span>
                </div>
                {accuracyTrend.length > 0 ? (
                  <div className="space-y-3">
                    <MiniSparkline data={accuracyTrend} color="#3b82f6" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{analytics.sessions.length} {t('dashboard.sessions')}</span>
                      <span>{t('dashboard.best')}: {analytics.bestScore}%</span>
                      <span>{t('dashboard.latest')}: {analytics.latestScore}%</span>
                    </div>
                  </div>
                ) : <p className="text-sm text-muted-foreground py-8 text-center">{t('dashboard.completeSessions')}</p>}
              </div>

              {/* Questions Solved */}
              <div className="rounded-2xl border border-border bg-card p-6 shadow-premium animate-slide-up animate-stagger-2">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">{t('dashboard.questionsPerSession')}</h3>
                  <span className="text-xs text-muted-foreground">{analytics.totalQuestions} {t('dashboard.total')}</span>
                </div>
                {questionsPerSession.length > 0 ? (
                  <div className="space-y-3">
                    <BarChartMini data={questionsPerSession} color="#8b5cf6" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{t('dashboard.exam')}: {analytics.examSessions}</span>
                      <span>{t('dashboard.training')}: {analytics.trainingSessions}</span>
                      <span>{t('dashboard.avgPerQ')}: {formatDuration(analytics.averageSecondsPerQuestion)}/q</span>
                    </div>
                  </div>
                ) : <p className="text-sm text-muted-foreground py-8 text-center">{t('dashboard.startPracticing')}</p>}
              </div>
            </div>

            {/* ═══ PMI ECO Breakdown ═══ */}
            <div className="grid lg:grid-cols-3 gap-4">
              {ecoDomains.map((eco, i) => {
                const stats = domainStats[eco.key] || { correct: 0, total: 0 };
                const acc = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;
                return (
                  <div key={eco.key} className={`rounded-2xl border border-border bg-card p-5 shadow-premium hover:shadow-premium-lg transition-all duration-300 animate-slide-up animate-stagger-${i + 1}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${eco.color} flex items-center justify-center text-lg shadow-md`}>{eco.icon}</div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-sm">{eco.label}</h4>
                        <p className="text-xs text-muted-foreground">{eco.pct}% {t('dashboard.ofExam')}</p>
                      </div>
                    </div>
                    <div className="flex items-end justify-between mb-2">
                      <span className="text-2xl font-bold">{acc}%</span>
                      <span className="text-xs text-muted-foreground">{stats.correct}/{stats.total} {t('dashboard.correct')}</span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div className={`h-full rounded-full bg-gradient-to-r ${eco.color} transition-all duration-1000`} style={{ width: `${acc}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ═══ Knowledge Area Heatmap + AI Coach ═══ */}
            <div className="grid lg:grid-cols-3 gap-4">
              {/* Heatmap */}
              <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-6 shadow-premium animate-slide-up animate-stagger-1">
                <h3 className="font-semibold mb-4">{t('dashboard.knowledgeAreaPerformance')}</h3>
                {Object.keys(domainStats).length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {Object.entries(domainStats).sort((a, b) => b[1].total - a[1].total).map(([dom, s]) => {
                      const acc = s.total > 0 ? Math.round((s.correct / s.total) * 100) : 0;
                      const heatColor = acc >= 80 ? 'bg-emerald-500/80 text-white' : acc >= 60 ? 'bg-amber-500/70 text-white' : acc >= 40 ? 'bg-orange-500/60 text-white' : 'bg-red-500/50 text-white';
                      return (
                        <div key={dom} className={`rounded-xl p-3 ${heatColor} transition-all hover:scale-105 cursor-default`}>
                          <p className="text-xs font-medium truncate">{dom}</p>
                          <p className="text-lg font-bold">{acc}%</p>
                          <p className="text-[10px] opacity-80">{s.correct}/{s.total}</p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {['People', 'Process', 'Business Environment', 'Delivery', 'Planning', 'Agile'].map(d => (
                      <div key={d} className="rounded-xl p-3 bg-muted/40">
                        <p className="text-xs font-medium text-muted-foreground truncate">{d}</p>
                        <p className="text-lg font-bold text-muted-foreground">—</p>
                        <p className="text-[10px] text-muted-foreground">{t('dashboard.noData')}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* AI Study Coach */}
              <div className="rounded-2xl border border-border bg-gradient-to-br from-violet-500/10 to-blue-500/5 p-6 shadow-premium animate-slide-up animate-stagger-2">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center text-white text-sm">🤖</div>
                  <h3 className="font-semibold">{t('dashboard.aiStudyCoach')}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">{coachAdvice}</p>
                <div className="space-y-2">
                  <Link to="/exam" className="block w-full text-center px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity">
                    {analytics.totalSessions === 0 ? t('dashboard.startFirstSession') : t('dashboard.continuePracticing')}
                  </Link>
                  {failedQuestions.length > 0 && (
                    <Link to="/exam" state={{ failedQuestionIds: failedQuestions.map(q => q.question_id || q.questionId), reference: dashboardReference }} className="block w-full text-center px-4 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-accent transition-colors">
                      {t('dashboard.reviewFailed', { count: failedQuestions.length })}
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* ═══ Recent Sessions + Failed Questions ═══ */}
            <div className="grid lg:grid-cols-2 gap-4">
              {/* Recent Sessions */}
              {analytics.sessions.length > 0 && (
                <div className="rounded-2xl border border-border bg-card p-6 shadow-premium animate-slide-up animate-stagger-1">
                  <h3 className="font-semibold mb-4">{t('dashboard.recentSessions')}</h3>
                  <div className="space-y-2">
                    {analytics.sessions.slice(0, 5).map((session) => (
                      <div key={session.id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xs font-bold ${session.mode === 'exam' ? 'bg-blue-500/15 text-blue-600 dark:text-blue-400' : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'}`}>
                          {session.mode === 'exam' ? 'EX' : 'TR'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium capitalize">{session.mode} · {session.score}%</p>
                          <p className="text-xs text-muted-foreground">{session.correct} {t('dashboard.correct')} · {session.incorrect} wrong · {formatDuration(session.average_seconds_per_question)}/q</p>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(session.created_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Review Questions — Failed / Not Answered with Overall/Session filter */}
              <div className="rounded-2xl border border-border bg-card p-6 shadow-premium animate-slide-up animate-stagger-2">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">
                    {reviewFilter === 'failed' ? t('dashboard.failedQuestions') : t('dashboard.notAnsweredQuestions')}
                  </h3>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${reviewFilter === 'failed' ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'}`}>
                    {filteredReviewQuestions.length} {t('dashboard.toReview')}
                  </span>
                </div>

                {/* Filter tabs: Failed / Not Answered */}
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => setReviewFilter('failed')}
                    className={`flex-1 px-3 py-1.5 text-xs rounded-lg border transition-colors font-medium ${reviewFilter === 'failed' ? 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30' : 'hover:bg-accent border-border'}`}
                  >
                    {t('dashboard.failedTab')} ({failedQuestions.length})
                  </button>
                  <button
                    onClick={() => setReviewFilter('notAnswered')}
                    className={`flex-1 px-3 py-1.5 text-xs rounded-lg border transition-colors font-medium ${reviewFilter === 'notAnswered' ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30' : 'hover:bg-accent border-border'}`}
                  >
                    {t('dashboard.notAnsweredTab')} ({notAnsweredQuestions.length})
                  </button>
                </div>

                {/* Scope filter: Overall / Per Session */}
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => { setScopeFilter('overall'); setSelectedSessionId('all'); }}
                    className={`flex-1 px-3 py-1.5 text-xs rounded-lg border transition-colors font-medium ${scopeFilter === 'overall' ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent border-border'}`}
                  >
                    {t('dashboard.overall')}
                  </button>
                  <button
                    onClick={() => setScopeFilter('session')}
                    className={`flex-1 px-3 py-1.5 text-xs rounded-lg border transition-colors font-medium ${scopeFilter === 'session' ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent border-border'}`}
                  >
                    {t('dashboard.perSession')}
                  </button>
                </div>

                {/* Session selector (only when per-session scope is selected) */}
                {scopeFilter === 'session' && analytics.sessions.length > 0 && (
                  <div className="mb-3">
                    <select
                      value={selectedSessionId}
                      onChange={(e) => setSelectedSessionId(e.target.value)}
                      className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="all">{t('dashboard.allSessions')}</option>
                      {analytics.sessions.map((session, idx) => (
                        <option key={session.id || idx} value={session.session_id || session.id || idx}>
                          {session.mode === 'exam' ? 'EX' : 'TR'} · {session.score}% · {new Date(session.created_at).toLocaleDateString()}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Filtered question list */}
                {filteredReviewQuestions.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">
                    {reviewFilter === 'failed' ? t('dashboard.noFailedQuestions') : t('dashboard.noNotAnswered')}
                  </p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {filteredReviewQuestions.slice(0, 20).map((q, index) => (
                      <Link key={`${q.id || q.question_id || q.questionId}-${index}`} to="/exam" state={{ targetQuestionId: q.question_id || q.questionId, reference: dashboardReference }} className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors group">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${reviewFilter === 'failed' ? 'bg-red-500/15 text-red-600 dark:text-red-400' : 'bg-amber-500/15 text-amber-600 dark:text-amber-400'}`}>
                          {reviewFilter === 'failed' ? '✕' : '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-muted-foreground truncate">{q.question_id || q.questionId || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">{formatDuration(q.time_spent_seconds || q.timeSpent || 0)} · {q.domain || 'Unknown'}</p>
                        </div>
                        <span className="text-xs text-primary group-hover:underline">{t('dashboard.retry')} →</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
